import { cn, formatPercent } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AlertTriangle, Bell, CheckCircle, Plus, ChevronDown, ChevronRight, Eye, FastForward, FileText, Info, Loader2, Lock, MessagesSquare, Play, Receipt, RefreshCw, SkipForward, Users, BookOpen, GraduationCap, Layers, School, Settings2, X, Clock, Blocks, Cog, FolderOpen } from 'lucide-react';
import { Fragment, useState, type ComponentType, type MouseEvent, type ReactNode } from 'react';
import type { MigrationStepExecution, StepExecutionStatus } from '../types';
import { LOTS_WITH_MAPPING } from './lotMappingRegistry';
import { LOTS_WITH_MAPPING_RULES, LOT_WARNINGS } from './lotMappingRulesRegistry';
import { MappingRulesDialog } from './MappingRulesDialog';
import { FieldValueJobsRow } from './FieldValueJobsRow';
import {
  bucketOfStep,
  isLotEmpty,
  pendingSourceAdditions,
  type LotStatusBucket,
} from './lotStatus';
import { isCategoryComingSoon, isCategoryOutOfPerimeter, isStepComingSoon, isStepOutOfPerimeter, isUserLotCode } from './modulePerimeter';

// NB : le caractère « réconciliable » d'un lot (re-run possible sur un lot déjà
// `completed`) est désormais porté par le back via `stepDefinition.reconcilable`
// (source de vérité unique : `Extractor::isReconcilable`). Plus de liste blanche
// dupliquée ici.

/**
 * Actions attachées au header d'un groupe de lots (ex: bouton drawer
 * « Mappings rôles » sur le groupe « Utilisateurs »). Le parent fournit la
 * config par catégorie.
 */
export interface CategoryAction {
  label: string;
  /** Catégorie ciblée (ex: 'users'). null = uncategorized. */
  category: string | null;
  onClick: () => void;
  /** Tooltip optionnel (défaut = label). */
  title?: string;
  /**
   * Complétude du mapping associé (ex: rôles). Si fourni, le chip affiche
   * `label X/Y` avec la couleur d'état, comme les chips de mapping par lot.
   */
  mapping?: { total: number; mapped: number };
}

const CATEGORY_META: Record<string, { label: string; Icon: ComponentType<{ className?: string }> }> = {
  configuration: { label: 'Réglages généraux', Icon: Cog },
  users: { label: 'Utilisateurs', Icon: Users },
  school_structure: { label: "Gestion de l'école", Icon: School },
  time_management: { label: 'Gestion du temps', Icon: Clock },
  evaluations: { label: 'Évaluations et bulletins', Icon: GraduationCap },
  billing: { label: 'Facturation & Comptabilité', Icon: Receipt },
  montessori: { label: 'Montessori', Icon: Blocks },
  messaging: { label: 'Communication', Icon: MessagesSquare },
  documents: { label: 'GED & documents', Icon: FileText },
  student_records: { label: "Dossier de l'élève", Icon: FolderOpen },
  notifications: { label: 'Notifications', Icon: Bell },
};

const UNCATEGORIZED_META = { label: 'Autres', Icon: Layers };

function getCategoryMeta(category: string | null | undefined) {
  if (!category) return UNCATEGORIZED_META;
  return CATEGORY_META[category] ?? { label: category, Icon: Layers };
}

interface CategoryGroup {
  category: string | null;
  steps: MigrationStepExecution[];
}

/**
 * Lots « actifs » d'un groupe : exclut les lots « disponibles prochainement »
 * (désactivés produit, affichés grisés). Les agrégats de groupe, le calcul
 * « lançable/bloqué » et l'accordéon initial ne portent que sur eux.
 */
function activeStepsOf(steps: MigrationStepExecution[]): MigrationStepExecution[] {
  return steps.filter(
    (s) => !isStepComingSoon(s.stepDefinition.category, s.stepDefinition.code)
  );
}

/** Chip grisé « Disponible prochainement » (groupe ou lot désactivé). */
function ComingSoonChip() {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[10px] font-medium normal-case tracking-normal text-gray-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-500 whitespace-nowrap">
      <Clock className="h-3 w-3" />
      Disponible prochainement
    </span>
  );
}

/** Ordre canonique des groupes = ordre d'insertion dans `CATEGORY_META`. */
const CATEGORY_ORDER = Object.keys(CATEGORY_META);

/**
 * Regroupe les lots par catégorie, dans l'ordre canonique de `CATEGORY_META`.
 *
 * Les groupes déclarés dans `CATEGORY_META` sont TOUJOURS rendus, même sans
 * aucun lot (placeholder « 0 lot ») — c'est ce qui rend visibles les groupes
 * vides comme « Configuration ». À l'inverse, un groupe qui possède des lots
 * mais dont aucun ne passe le filtre courant reste masqué (comportement de
 * filtre inchangé). `filtered` = lots après filtre, `all` = tous les lots du
 * projet (sert à distinguer « groupe réellement vide » de « masqué par filtre »).
 */
function groupByCategory(
  filtered: MigrationStepExecution[],
  all: MigrationStepExecution[]
): CategoryGroup[] {
  const keyOf = (s: MigrationStepExecution) => s.stepDefinition.category ?? '__none__';
  const catsWithLots = new Set(all.map(keyOf));

  const filteredByCat = new Map<string, MigrationStepExecution[]>();
  for (const step of filtered) {
    const cat = keyOf(step);
    (filteredByCat.get(cat) ?? filteredByCat.set(cat, []).get(cat)!).push(step);
  }

  // Ordre : catégories déclarées (ordre CATEGORY_META), puis catégories
  // inconnues présentes dans les lots, puis « non catégorisé » en dernier.
  const order = [...CATEGORY_ORDER];
  for (const cat of catsWithLots) {
    if (cat !== '__none__' && !order.includes(cat)) order.push(cat);
  }
  order.push('__none__');

  const result: CategoryGroup[] = [];
  for (const key of order) {
    const steps = filteredByCat.get(key) ?? [];
    const isDeclaredEmpty = !catsWithLots.has(key) && key in CATEGORY_META;
    // Rendu si : au moins un lot passe le filtre, OU c'est un groupe déclaré
    // sans aucun lot (placeholder permanent).
    if (steps.length > 0 || isDeclaredEmpty) {
      result.push({ category: key === '__none__' ? null : key, steps });
    }
  }
  return result;
}

/**
 * Statuts d'un lot considérés comme « satisfaisant une dépendance ».
 * `bypassed` = contournement explicite de l'opérateur : les lots suivants se
 * débloquent même si celui-ci est incomplet (il reste relançable plus tard).
 */
const DEP_SATISFIED: StepExecutionStatus[] = ['completed', 'completed_with_errors', 'bypassed'];

/**
 * Une dépendance est satisfaite si le lot est migré (ou contourné), OU s'il
 * est vide (0 ligne V1, comptage fait) : rien à migrer ne doit pas bloquer.
 * Ex. école sans regroupement de matières — le lot Matières retombe sur le
 * groupe par défaut « Général ». Miroir de MigrationRunner::findBlockingDependencies.
 */
function isDepSatisfied(dep: MigrationStepExecution | undefined): boolean {
  if (!dep) return false;
  if (isLotEmpty(dep)) return true;
  return DEP_SATISFIED.includes(dep.status);
}

/**
 * Renvoie les dépendances d'un lot qui ne sont PAS encore satisfaites.
 * Un lot est « bloqué » tant que cette liste n'est pas vide. Calcul
 * entièrement client à partir des statuts des lots frères du projet.
 */
function blockingDepsOf(
  step: MigrationStepExecution,
  byCode: Map<string, MigrationStepExecution>
): { code: string; name: string }[] {
  const deps = step.stepDefinition.dependencies ?? [];
  const blocking: { code: string; name: string }[] = [];
  for (const code of deps) {
    const dep = byCode.get(code);
    if (!isDepSatisfied(dep)) {
      blocking.push({ code, name: dep?.stepDefinition.name ?? code });
    }
  }
  return blocking;
}

/**
 * Toutes les dépendances d'un lot avec leur état (satisfaite ou non), pour
 * affichage en tooltip. À l'inverse de `blockingDepsOf`, on retourne TOUTES
 * les dépendances (pas seulement les bloquantes) afin que l'admin voie les
 * prérequis du lot même quand ils sont déjà migrés.
 */
function depsOf(
  step: MigrationStepExecution,
  byCode: Map<string, MigrationStepExecution>
): { code: string; name: string; satisfied: boolean }[] {
  return (step.stepDefinition.dependencies ?? []).map((code) => {
    const dep = byCode.get(code);
    return {
      code,
      name: dep?.stepDefinition.name ?? code,
      satisfied: isDepSatisfied(dep),
    };
  });
}

/**
 * Tooltip Radix générique : enveloppe `children` (le déclencheur) et affiche
 * `content` au survol. Si `content` est vide, l'élément est rendu tel quel,
 * sans tooltip.
 *
 * `wrap` enveloppe le déclencheur dans un `<span>` — nécessaire pour un bouton
 * désactivé (`disabled:pointer-events-none` → l'événement remonte au span).
 * Pour un élément déjà survolable (div, span, bouton actif), on attache le
 * trigger directement via `asChild` afin de préserver le layout (ex. une barre
 * de progression en `w-full`).
 */
function InfoTooltip({
  content,
  children,
  wrap = false,
  contentClassName,
}: {
  content: ReactNode;
  children: ReactNode;
  wrap?: boolean;
  contentClassName?: string;
}) {
  if (!content) return <>{children}</>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {wrap ? <span className="inline-flex">{children}</span> : children}
      </TooltipTrigger>
      <TooltipContent className={contentClassName}>{content}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Contenu du tooltip de dépendances : raison de blocage optionnelle (en rouge)
 * + liste des dépendances avec leur état (✓ migrée / ✗ à migrer). `null` si le
 * lot n'a ni dépendance ni raison de blocage.
 */
function depsTooltipContent(
  deps: { code: string; name: string; satisfied: boolean }[],
  blockReason?: string
): ReactNode {
  if (deps.length === 0 && !blockReason) return null;
  return (
    <>
      {blockReason && (
        <p className="mb-1.5 font-medium text-red-600 dark:text-red-400">{blockReason}</p>
      )}
      {deps.length > 0 && (
        <>
          <p className="mb-1 font-medium uppercase tracking-wide text-[10px] text-slate-400 dark:text-slate-500">
            Dépendances
          </p>
          <ul className="space-y-1">
            {deps.map((d) => (
              <li key={d.code} className="flex items-center gap-1.5">
                {d.satisfied ? (
                  <CheckCircle className="h-3 w-3 shrink-0 text-emerald-500" />
                ) : (
                  <X className="h-3 w-3 shrink-0 text-red-500" />
                )}
                <span className={d.satisfied ? '' : 'font-medium'}>{d.name}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

/**
 * Tooltip listant les dépendances d'un lot (avec leur état), précédé d'une
 * raison de blocage optionnelle. Enveloppe le déclencheur dans un `<span>` pour
 * que le survol marche aussi sur un bouton désactivé.
 */
function DepsTooltip({
  deps,
  blockReason,
  children,
}: {
  deps: { code: string; name: string; satisfied: boolean }[];
  blockReason?: string;
  children: ReactNode;
}) {
  return (
    <InfoTooltip content={depsTooltipContent(deps, blockReason)} wrap>
      {children}
    </InfoTooltip>
  );
}

/**
 * Un lot est « bloqué » si une dépendance n'est pas satisfaite OU si son
 * mapping référentiel est incomplet. Calcul partagé entre l'affichage du lot
 * et celui du groupe.
 */
function isStepBlocked(
  step: MigrationStepExecution,
  byCode: Map<string, MigrationStepExecution>,
  mappingStatusByCode?: Record<string, { total: number; mapped: number }>
): boolean {
  if (blockingDepsOf(step, byCode).length > 0) return true;
  if (!LOTS_WITH_MAPPING.has(step.stepDefinition.code)) return false;
  const ms = mappingStatusByCode?.[step.stepDefinition.code];
  return !!ms && ms.total > 0 && ms.mapped < ms.total;
}

/**
 * Raison de blocage d'un lot, formatée pour tooltip (sans préfixe « Bloqué »).
 * `null` si le lot n'est pas bloqué. Combine mapping incomplet et dépendances
 * non satisfaites, dans le même ordre que l'affichage du lot.
 */
function stepBlockReason(
  step: MigrationStepExecution,
  byCode: Map<string, MigrationStepExecution>,
  mappingStatusByCode?: Record<string, { total: number; mapped: number }>
): string | null {
  const reasons: string[] = [];
  const ms = mappingStatusByCode?.[step.stepDefinition.code];
  if (LOTS_WITH_MAPPING.has(step.stepDefinition.code) && ms && ms.total > 0 && ms.mapped < ms.total) {
    reasons.push(`mapping incomplet (${ms.mapped}/${ms.total})`);
  }
  const blocking = blockingDepsOf(step, byCode);
  if (blocking.length > 0) {
    reasons.push(`à migrer d'abord : ${blocking.map((b) => b.name).join(', ')}`);
  }
  return reasons.length > 0 ? reasons.join(' · ') : null;
}

type MappingChipState = 'na' | 'none' | 'partial' | 'complete';

/** Dérive l'état d'un mapping (`X/Y`) en une des 4 catégories visuelles. */
function mappingChipState(ms?: { total: number; mapped: number } | null): MappingChipState {
  if (!ms || ms.total === 0) return 'na';
  if (ms.mapped === 0) return 'none';
  if (ms.mapped < ms.total) return 'partial';
  return 'complete';
}

/** Classes du chip de mapping (couleur selon l'état). Visuel partagé lot/groupe. */
function mappingChipClass(state: MappingChipState): string {
  return state === 'complete'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
    : state === 'partial'
      ? 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100'
      : state === 'none'
        ? 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
        : 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100';
}

interface GroupAggregate {
  total: number;
  migrated: number;
  review: number;
  failed: number;
  /** Records exclus de la migration (comptent comme traités). */
  excluded: number;
  /** Lignes V1 ajoutées depuis la dernière migration des lots du groupe. */
  additions: number;
  /** Statut représentatif du groupe (priorité : en cours > erreur > à vérifier > migré > à migrer). */
  bucket: LotStatusBucket;
}

function aggregateGroup(steps: MigrationStepExecution[]): GroupAggregate {
  let total = 0;
  let migrated = 0;
  let review = 0;
  let failed = 0;
  let excluded = 0;
  let additions = 0;
  const counts: Record<LotStatusBucket, number> = {
    pending: 0,
    running: 0,
    review: 0,
    failed: 0,
    completed: 0,
  };
  for (const s of steps) {
    total += s.totalRecords;
    migrated += s.successRecords;
    review += s.pendingReviewRecords;
    failed += s.failedRecords;
    excluded += s.excludedRecords ?? 0;
    additions += pendingSourceAdditions(s);
    // Un lot estimé à 0 ligne (« aucune donnée ») n'a rien à migrer : on le
    // compte comme terminé pour que le groupe puisse atteindre « Migré ».
    counts[isLotEmpty(s) ? 'completed' : bucketOfStep(s)]++;
  }
  let bucket: LotStatusBucket;
  if (counts.running > 0) bucket = 'running';
  else if (counts.failed > 0) bucket = 'failed';
  else if (counts.review > 0) bucket = 'review';
  else if (steps.length > 0 && counts.completed === steps.length) bucket = 'completed';
  else bucket = 'pending';
  return { total, migrated, review, failed, excluded, additions, bucket };
}

/**
 * Pastille « + N à migrer » : lignes présentes en V1 mais jamais extraites,
 * donc saisies depuis la dernière exécution du lot. Rendue à côté de la barre
 * de progression pour que l'écart V1/V2 saute aux yeux à l'ouverture du
 * dossier, sans avoir à relancer un comptage.
 */
function AdditionsChip({ count, countedAt }: { count: number; countedAt?: string | null }) {
  return (
    <InfoTooltip
      wrap
      contentClassName="max-w-xs"
      content={
        <>
          <p>
            <span className="font-medium tabular-nums">{count}</span> ligne{count > 1 ? 's' : ''}{' '}
            présente{count > 1 ? 's' : ''} en V1 mais jamais migrée{count > 1 ? 's' : ''} : donnée
            saisie après la dernière exécution du lot. « Relancer » les reprend — extraction
            additive et idempotente (rapprochement sur l'identifiant V1), sans doublon ni reset.
          </p>
          {countedAt && (
            <p className="mt-1 text-[11px] opacity-70">
              Comptage V1 du {new Date(countedAt).toLocaleString('fr-FR')}
            </p>
          )}
        </>
      }
    >
      <span className="inline-flex shrink-0 cursor-help items-center gap-1 rounded-md border border-orange-200 bg-orange-50 px-1.5 py-0.5 text-[11px] font-semibold leading-none tabular-nums text-orange-700 dark:border-orange-700/50 dark:bg-orange-900/20 dark:text-orange-400">
        <Plus className="h-3 w-3" />
        {count} à migrer
      </span>
    </InfoTooltip>
  );
}

const STATUS_CONFIG: Record<
  LotStatusBucket,
  { label: string; pill: string; dot: string }
> = {
  pending: {
    label: 'À migrer',
    pill: 'bg-gray-100 text-gray-600 border-gray-200',
    dot: 'bg-gray-400',
  },
  running: {
    label: 'En cours',
    pill: 'bg-orange-50 text-orange-700 border-orange-200',
    dot: 'bg-orange-500',
  },
  review: {
    label: 'À vérifier',
    pill: 'bg-amber-50 text-amber-700 border-amber-200',
    dot: 'bg-amber-500',
  },
  failed: {
    label: 'En erreur',
    pill: 'bg-red-50 text-red-700 border-red-200',
    dot: 'bg-red-500',
  },
  completed: {
    label: 'Migré',
    pill: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dot: 'bg-emerald-500',
  },
};

interface LotsTableProps {
  steps: MigrationStepExecution[];
  /**
   * Tous les lots du projet (non filtrés). Sert à résoudre l'état des
   * dépendances même quand un prérequis est masqué par le filtre courant.
   * Par défaut = `steps`.
   */
  allSteps?: MigrationStepExecution[];
  /**
   * Lance (ou relance) un lot. `reconcile=true` (lots réconciliants type
   * `school.student_groups`) ré-ouvre les records déjà migrés pour rejouer le
   * diff additif sur l'existant.
   */
  onRun: (step: MigrationStepExecution, reconcile?: boolean) => void;
  onView: (step: MigrationStepExecution) => void;
  runningStepId: number | null;
  canRun: boolean;
  /**
   * Autorise le re-run « réconciliation » d'un lot déjà `completed`. Distinct
   * de `canRun` : la réconciliation vise justement les migrations terminées,
   * donc elle reste possible quand le projet est en statut `completed` (là où
   * `canRun` est faux pour empêcher toute nouvelle migration).
   */
  canReconcile: boolean;
  /** Actions facultatives par catégorie, affichées dans le header du groupe. */
  categoryActions?: CategoryAction[];
  /**
   * Handler appelé quand l'admin clique sur l'icône `Settings2` d'une ligne
   * de lot dont le code est dans `LOTS_WITH_MAPPING`. Sert à ouvrir le drawer
   * de mapping V1 → V2 normalisé du lot.
   */
  onConfigureMapping?: (step: MigrationStepExecution) => void;
  /**
   * Handler appelé pour estimer le volume d'un seul lot (COUNT V1 sur sa
   * sourceQuery). Si non fourni, l'icône d'estimation n'est pas affichée.
   */
  onEstimateLot?: (step: MigrationStepExecution) => Promise<void> | void;
  /** Lot en cours d'estimation (spinner). Si null, aucun spinner. */
  estimatingStepId?: number | null;
  /**
   * État de complétude du mapping référentiel V1 → V2, par code de lot
   * (seulement pour les lots de `LOTS_WITH_MAPPING`). `total` = nombre de
   * valeurs V1 distinctes ; `mapped` = combien sont associées. Un lot dont le
   * mapping est partiel ou absent est bloqué pour la migration.
   */
  mappingStatusByCode?: Record<string, { total: number; mapped: number }>;
  /**
   * Contourne un lot incomplet (statut `bypassed`) : les lots dépendants se
   * débloquent. Le handler est responsable de la dialog de confirmation.
   * Si non fourni, le bouton « Bypasser » n'est pas affiché.
   */
  onBypass?: (step: MigrationStepExecution) => void;
  /** Lance la migration séquentielle de tous les lots d'un groupe (catégorie). */
  onRunGroup?: (category: string | null) => void;
  /** Demande l'arrêt de l'enchaînement de groupe en cours. */
  onAbortGroup?: () => void;
  /** Catégorie du groupe en cours de migration (clé `__none__` si non catégorisé). */
  runningGroup?: string | null;
  /** Info d'arrêt de l'enchaînement (échec ou blocage), pour affichage sur la card. */
  groupStop?: { category: string | null; stepId: number; reason: 'failed' | 'blocked'; message: string } | null;
  /**
   * Codes des modules V2 souscrits par l'école. Un lot dont le module n'est pas
   * souscrit (celui de sa catégorie, ou ceux de sa surcharge par code) est
   * « hors périmètre » : il est masqué et exclu de l'avancement — et son groupe
   * disparaît si aucun de ses lots n'est en périmètre. `null`/`undefined` ⇒
   * aucun gating.
   */
  activeV2Modules?: string[] | null;
}

export function LotsTable({ steps, allSteps, onRun, onView, runningStepId, canRun, canReconcile, categoryActions = [], onConfigureMapping, onEstimateLot, estimatingStepId, mappingStatusByCode, onBypass, onRunGroup, onAbortGroup, runningGroup, groupStop, activeV2Modules }: LotsTableProps) {
  // Hors périmètre : le module V2 du lot (surcharge par code) ou de sa
  // catégorie n'est pas souscrit par l'école → le lot est masqué et exclu de
  // l'avancement global (voir stats côté page + board card back). Un groupe
  // dont AUCUN lot n'est en périmètre disparaît entièrement, placeholder
  // « 0 lot » compris ; un groupe dont un seul lot est en périmètre (ex. le
  // référentiel de compétences sur une école Montessori sans module Évaluation)
  // reste affiché avec ce seul lot.
  const inPerimeter = (s: MigrationStepExecution) =>
    !isStepOutOfPerimeter(s.stepDefinition.category, s.stepDefinition.code, activeV2Modules);
  const groups = groupByCategory(
    steps.filter(inPerimeter),
    (allSteps ?? steps).filter(inPerimeter)
  ).filter((g) => g.steps.length > 0 || !isCategoryOutOfPerimeter(g.category, activeV2Modules));
  // État initial de l'accordéon : tous les groupes fermés, sauf le PREMIER qui
  // nécessite encore une migration (non terminé — pending/bloqué/en erreur/à
  // vérifier/en cours). Calculé une seule fois au montage (LotsTable n'est monté
  // qu'après chargement des lots) ; l'admin garde ensuite la main.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    const firstToMigrate = groups.find((g) => {
      // Un groupe « disponible prochainement » n'est jamais le groupe ouvert,
      // et les lots désactivés ne comptent pas dans « reste à migrer ».
      if (isCategoryComingSoon(g.category)) return false;
      const active = activeStepsOf(g.steps);
      return active.length > 0 && aggregateGroup(active).bucket !== 'completed';
    });
    const openKey = firstToMigrate ? firstToMigrate.category ?? '__none__' : null;
    return new Set(groups.map((g) => g.category ?? '__none__').filter((k) => k !== openKey));
  });
  // Lot dont on affiche les règles de mapping (dialog informatif). Null = fermé.
  const [rulesStep, setRulesStep] = useState<MigrationStepExecution | null>(null);

  // Index code → lot (sur l'ensemble non filtré), pour résoudre l'état des
  // dépendances côté client.
  const byCode = new Map<string, MigrationStepExecution>(
    (allSteps ?? steps).map((s) => [s.stepDefinition.code, s])
  );

  const toggleCategory = (cat: string | null) => {
    const key = cat ?? '__none__';
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const isCollapsed = (cat: string | null) => collapsed.has(cat ?? '__none__');

  return (
    <TooltipProvider delayDuration={150}>
    <Table className="lots-table table-fixed min-w-[960px]">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="text-[11px] font-bold uppercase tracking-wider text-gray-500 w-80">
              Lot
            </TableHead>
            <TableHead className="text-[11px] font-bold uppercase tracking-wider text-gray-500 w-48 text-center">
              Mapping
            </TableHead>
            <TableHead className="text-[11px] font-bold uppercase tracking-wider text-gray-500 w-full text-center">
              Progression
            </TableHead>
            <TableHead className="text-[11px] font-bold uppercase tracking-wider text-gray-500 w-32 text-center">
              Statut
            </TableHead>
            <TableHead className="text-[11px] font-bold uppercase tracking-wider text-gray-500 w-44 text-center">
              Action
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center py-12 text-gray-500">
                Aucun lot ne correspond à ce filtre.
              </TableCell>
            </TableRow>
          ) : (
            groups.flatMap((group) => {
              const meta = getCategoryMeta(group.category);
              const HeaderIcon = meta.Icon;

              // Groupe « disponible prochainement » : header grisé, non
              // dépliable, sans progression ni action. Les lots restent
              // visibles côté data mais ne sont jamais rendus.
              if (isCategoryComingSoon(group.category)) {
                return [
                  <TableRow
                    key={`cat-${group.category ?? 'none'}`}
                    className="lot-group-row lot-group-row-disabled select-none"
                  >
                    <TableCell className="py-2">
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        {/* espace réservé du chevron, pour l'alignement */}
                        <span className="h-4 w-4 shrink-0" />
                        <HeaderIcon className="h-4 w-4 shrink-0 text-[hsl(217,30%,75%)] dark:text-slate-600" />
                        <span className="text-xs font-bold uppercase tracking-normal text-[hsl(217,30%,65%)] dark:text-slate-500 whitespace-nowrap">
                          {meta.label}
                        </span>
                        <span className="text-xs text-[hsl(217,30%,75%)] dark:text-slate-600 shrink-0 whitespace-nowrap">
                          · {group.steps.length} {group.steps.length > 1 ? 'lots' : 'lot'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="py-2 text-center">
                      <span className="text-xs text-gray-300 dark:text-slate-600">—</span>
                    </TableCell>
                    <TableCell className="py-2">
                      <div className="flex items-center justify-center w-full">
                        <ComingSoonChip />
                      </div>
                    </TableCell>
                    <TableCell className="py-2 text-center">
                      <span className="text-xs text-gray-300 dark:text-slate-600">—</span>
                    </TableCell>
                    <TableCell className="py-2" />
                  </TableRow>,
                ];
              }

              const collapsedNow = isCollapsed(group.category);
              const Chevron = collapsedNow ? ChevronRight : ChevronDown;
              const actionsForGroup = categoryActions.filter((a) => a.category === group.category);
              const stopBubble = (e: MouseEvent) => e.stopPropagation();
              // Agrégats et « lançable/bloqué » calculés hors lots désactivés
              // (« disponibles prochainement »), pour que le groupe puisse
              // atteindre « Migré » malgré un lot grisé.
              const activeGroupSteps = activeStepsOf(group.steps);
              const agg = aggregateGroup(activeGroupSteps);
              const aggCfg = STATUS_CONFIG[agg.bucket];
              const aggDone = agg.migrated + agg.review + agg.failed + agg.excluded;
              // Même convention que la ligne de lot : % = taux de réussite
              // (succès / total hors exclus), pas l'avancement du traitement.
              const aggEffectiveTotal = agg.total - agg.excluded;
              const aggPctSuccess =
                aggEffectiveTotal > 0 ? (agg.migrated / aggEffectiveTotal) * 100 : agg.total > 0 ? 100 : 0;
              const groupKey = group.category ?? '__none__';
              // Groupe déclaré sans aucun lot actif (placeholder) : pas de
              // progression, pas de statut « Bloqué », pas de bouton de migration.
              const isEmptyGroup = activeGroupSteps.length === 0;
              const isGroupRunning = runningGroup === groupKey;
              const groupHasRunnable = activeGroupSteps.some(
                (s) =>
                  // `bucketOfStep` et non le statut brut : un lot `completed`
                  // qui garde des lignes non traitées reste à relancer, et son
                  // groupe ne doit pas se déclarer « Bloqué » alors que la
                  // ligne, elle, propose bien un bouton.
                  bucketOfStep(s) !== 'completed' &&
                  s.status !== 'skipped' &&
                  s.status !== 'bypassed' &&
                  // Un lot « aucune donnée » (0 ligne estimée, rien de neuf
                  // côté V1) n'est pas à migrer.
                  !isLotEmpty(s) &&
                  // Un lot bloqué (dépendance/mapping) n'est pas lançable en l'état.
                  !isStepBlocked(s, byCode, mappingStatusByCode)
              );
              // Groupe « bloqué » : il reste des lots à traiter mais aucun n'est
              // lançable (tous bloqués par mapping/dépendances). On le signale
              // au lieu d'afficher « À migrer » + un bouton sans effet.
              const groupBlocked = !isEmptyGroup && agg.bucket === 'pending' && !groupHasRunnable;
              // Lots restants mais bloqués (mapping/dépendances) → détail du
              // tooltip « Bloqué » du groupe.
              const groupBlockedSteps = groupBlocked
                ? activeGroupSteps.filter(
                    (s) =>
                      bucketOfStep(s) !== 'completed' &&
                      s.status !== 'skipped' &&
                      s.status !== 'bypassed' &&
                      !isLotEmpty(s) &&
                      isStepBlocked(s, byCode, mappingStatusByCode)
                  )
                : [];
              const groupBlockedContent = groupBlocked ? (
                <>
                  <p className="mb-1.5 font-medium text-red-600 dark:text-red-400">
                    Groupe bloqué — aucun lot lançable
                  </p>
                  <ul className="space-y-1">
                    {groupBlockedSteps.map((s) => {
                      const reason = stepBlockReason(s, byCode, mappingStatusByCode);
                      return (
                        <li key={s.id} className="flex items-start gap-1.5">
                          <Lock className="mt-0.5 h-3 w-3 shrink-0 text-red-500" />
                          <span>
                            <span className="font-medium">{s.stepDefinition.name}</span>
                            {reason ? ` — ${reason}` : ''}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </>
              ) : null;
              const groupStopHere =
                groupStop && (groupStop.category ?? '__none__') === groupKey ? groupStop : null;
              const headerRow = (
                <TableRow
                  key={`cat-${group.category ?? 'none'}`}
                  onClick={() => toggleCategory(group.category)}
                  className="lot-group-row cursor-pointer select-none"
                >
                  {/* Groupe */}
                  <TableCell className="py-2">
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      <Chevron className="h-4 w-4 shrink-0 text-[hsl(217,60%,55%)] dark:text-[hsl(217,50%,60%)]" />
                      <HeaderIcon className="h-4 w-4 shrink-0 text-[hsl(217,60%,45%)] dark:text-[hsl(217,70%,70%)]" />
                      <span className="text-xs font-bold uppercase tracking-normal text-[hsl(217,60%,45%)] dark:text-[hsl(217,70%,70%)] whitespace-nowrap">
                        {meta.label}
                      </span>
                      <span className="text-xs text-[hsl(217,40%,65%)] dark:text-[hsl(217,30%,55%)] shrink-0 whitespace-nowrap">
                        · {group.steps.length} {group.steps.length > 1 ? 'lots' : 'lot'}
                      </span>
                      {agg.additions > 0 && (
                        <span onClick={stopBubble}>
                          <AdditionsChip count={agg.additions} />
                        </span>
                      )}
                    </div>
                  </TableCell>

                  {/* Colonne Mapping — chips de mapping niveau groupe (ex: rôles) */}
                  <TableCell className="py-2 text-center">
                    <div className="flex items-center justify-center gap-1.5" onClick={stopBubble}>
                      {actionsForGroup.map((a) => {
                        const st = mappingChipState(a.mapping);
                        return (
                          <InfoTooltip key={a.label} content={a.title ?? a.label} wrap>
                            <button
                              type="button"
                              onClick={a.onClick}
                              className={cn(
                                'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium normal-case tracking-normal transition-colors',
                                mappingChipClass(st)
                              )}
                            >
                              {st === 'complete' ? (
                                <CheckCircle className="h-3 w-3" />
                              ) : (
                                <Settings2 className="h-3 w-3" />
                              )}
                              {a.mapping ? `${a.label} ${a.mapping.mapped}/${a.mapping.total}` : a.label}
                            </button>
                          </InfoTooltip>
                        );
                      })}
                    </div>
                  </TableCell>

                  {/* Progression globale du groupe */}
                  <TableCell className="py-2">
                    {isEmptyGroup ? (
                      // « aucun lot » seulement si le groupe est réellement vide ;
                      // s'il ne contient que des lots « disponibles prochainement »
                      // (grisés en dessous), un tiret suffit.
                      <span className="block w-full text-center text-xs italic normal-case tracking-normal font-normal text-slate-400 dark:text-slate-500">
                        {group.steps.length > 0 ? '—' : 'aucun lot'}
                      </span>
                    ) : agg.total === 0 ? (
                      agg.additions > 0 ? (
                        <div className="flex items-center gap-2.5 w-full text-xs normal-case tracking-normal">
                          <span className="w-14 shrink-0 text-right font-semibold tabular-nums text-slate-900 dark:text-slate-100">
                            0
                            <span className="text-slate-500 dark:text-slate-400 font-normal">/{agg.additions}</span>
                          </span>
                          <div className="flex h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200/70 dark:bg-slate-700" />
                          <span className="w-14 shrink-0 text-right font-semibold tabular-nums text-slate-700 dark:text-slate-200">
                            0%
                          </span>
                        </div>
                      ) : (
                        <span
                          className={cn(
                            'block w-full text-center text-xs italic normal-case tracking-normal font-normal',
                            activeGroupSteps.every((s) => s.countEstimatedAt)
                              ? 'text-slate-400 dark:text-slate-500'
                              : 'text-slate-500 dark:text-slate-400'
                          )}
                        >
                          {activeGroupSteps.every((s) => s.countEstimatedAt) ? 'aucune donnée' : 'non estimé'}
                        </span>
                      )
                    ) : (
                      <div className="flex items-center gap-2.5 w-full text-xs normal-case tracking-normal">
                        <span className="w-14 shrink-0 text-right font-semibold tabular-nums text-slate-900 dark:text-slate-100">
                          {aggDone}
                          <span className="text-slate-500 dark:text-slate-400 font-normal">/{agg.total}</span>
                        </span>
                        <div className="flex h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200/70 dark:bg-slate-700">
                          {agg.migrated > 0 && (
                            <div
                              className={agg.migrated + agg.excluded === agg.total ? 'bg-emerald-500' : 'bg-orange-400'}
                              style={{ width: `${(agg.migrated / agg.total) * 100}%` }}
                            />
                          )}
                          {agg.review > 0 && (
                            <div className="bg-amber-500" style={{ width: `${(agg.review / agg.total) * 100}%` }} />
                          )}
                          {agg.failed > 0 && (
                            <div className="bg-red-500" style={{ width: `${(agg.failed / agg.total) * 100}%` }} />
                          )}
                          {agg.excluded > 0 && (
                            <div className="bg-slate-400" style={{ width: `${(agg.excluded / agg.total) * 100}%` }} />
                          )}
                        </div>
                        <span
                          className={cn(
                            'w-14 shrink-0 text-right font-semibold tabular-nums',
                            agg.failed > 0
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-slate-700 dark:text-slate-200'
                          )}
                        >
                          {formatPercent(aggPctSuccess)}%
                        </span>
                      </div>
                    )}
                  </TableCell>

                  {/* Statut global du groupe */}
                  <TableCell className="py-2 text-center">
                    {isEmptyGroup ? (
                      <span className="text-xs italic text-slate-400 dark:text-slate-500 normal-case tracking-normal font-normal">
                        —
                      </span>
                    ) : groupBlocked ? (
                      <InfoTooltip content={groupBlockedContent}>
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700 cursor-help">
                          <Lock className="h-3 w-3" />
                          Bloqué
                        </span>
                      </InfoTooltip>
                    ) : (
                      <span
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium',
                          aggCfg.pill
                        )}
                      >
                        <span className={cn('h-1.5 w-1.5 rounded-full', aggCfg.dot)} />
                        {aggCfg.label}
                      </span>
                    )}
                  </TableCell>

                  {/* Actions de groupe */}
                  <TableCell className="py-2">
                    <div className="flex items-center justify-center gap-1.5" onClick={stopBubble}>
                      {onRunGroup && groupHasRunnable && isGroupRunning && (
                        <>
                          <Button variant="outline" size="sm" className="h-6 py-0 w-40 whitespace-nowrap" disabled>
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Migration…
                          </Button>
                          {onAbortGroup && (
                            <InfoTooltip content="Arrêter l'enchaînement après le lot en cours" wrap>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 py-0 text-gray-500 hover:text-red-700"
                                onClick={onAbortGroup}
                              >
                                <X className="h-3.5 w-3.5" />
                                Annuler
                              </Button>
                            </InfoTooltip>
                          )}
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
              const groupErrorRow = groupStopHere ? (
                <TableRow key={`err-${groupKey}`} className="bg-red-50/60 dark:bg-red-950/20">
                  <TableCell colSpan={5} className="py-2">
                    <div className="flex items-center gap-2 text-sm text-red-700 dark:text-red-300 normal-case tracking-normal">
                      <AlertTriangle className="h-4 w-4 shrink-0" />
                      <span>{groupStopHere.message}</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : null;
              if (collapsedNow) {
                return groupErrorRow ? [headerRow, groupErrorRow] : [headerRow];
              }
              const stepRows = group.steps.map((step) => {
              // Lot « disponible prochainement » : ligne grisée, sans mapping,
              // progression, statut ni action.
              if (isStepComingSoon(step.stepDefinition.category, step.stepDefinition.code)) {
                return (
                  <TableRow key={step.id} className="lot-row border-b border-gray-100 dark:border-slate-700">
                    <TableCell className="py-0">
                      <div className="flex items-stretch">
                        <div className="ml-5 mr-4 w-px shrink-0 bg-gray-200 dark:bg-slate-700" />
                        <div className="min-w-0 flex items-center gap-1.5 py-1">
                          <span className="font-semibold text-sm leading-none text-gray-400 dark:text-slate-500 truncate">
                            {step.stepDefinition.name}
                          </span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-0 text-center">
                      <span className="text-xs text-gray-300 dark:text-slate-600">—</span>
                    </TableCell>
                    <TableCell className="py-0">
                      <div className="flex items-center justify-center w-full py-1">
                        <ComingSoonChip />
                      </div>
                    </TableCell>
                    <TableCell className="py-0 text-center">
                      <span className="text-xs text-gray-300 dark:text-slate-600">—</span>
                    </TableCell>
                    <TableCell className="py-0" />
                  </TableRow>
                );
              }
              const bucket = bucketOfStep(step);
              const cfg = STATUS_CONFIG[bucket];
              const isSkipped = step.status === 'skipped';
              const isBypassed = step.status === 'bypassed';
              const blocking = blockingDepsOf(step, byCode);

              // État de complétude du mapping référentiel (cycles, niveaux…).
              const needsMapping = LOTS_WITH_MAPPING.has(step.stepDefinition.code);
              const ms = mappingStatusByCode?.[step.stepDefinition.code];
              const mappingTotal = ms?.total ?? 0;
              const mappingMapped = ms?.mapped ?? 0;
              const mappingState: 'na' | 'none' | 'partial' | 'complete' =
                !needsMapping || !ms || mappingTotal === 0
                  ? 'na'
                  : mappingMapped === 0
                    ? 'none'
                    : mappingMapped < mappingTotal
                      ? 'partial'
                      : 'complete';
              const mappingIncomplete = mappingState === 'none' || mappingState === 'partial';

              // Lot estimé à 0 ligne (« aucune donnée ») : rien à migrer, donc
              // ni chip de statut ni bouton d'action.
              const isEmpty = isLotEmpty(step);

              // Lignes V1 apparues depuis la dernière extraction du lot
              // (comptage live, lots utilisateurs) → pastille « + N à migrer ».
              const additions = pendingSourceAdditions(step);

              // Liste complète des dépendances (avec état) → tooltip listant les
              // prérequis du lot, qu'il soit bloqué ou non.
              const deps = depsOf(step, byCode);

              const isBlocked = blocking.length > 0 || mappingIncomplete;
              // Raison de blocage affichée en tête du tooltip des éléments bloqués.
              const reason = stepBlockReason(step, byCode, mappingStatusByCode);
              const blockReason = reason ? `Bloqué — ${reason}` : undefined;

              // Lancement déclenché depuis cette table (clic Migrer/Reprendre/
              // Relancer). Sert à donner un feedback immédiat — bouton animé et
              // barre remise à 0 — tant que le serveur n'a pas encore basculé le
              // lot en « running » (premier chunk pas encore revenu).
              const isLocallyRunning = runningStepId === step.id;
              const isLocallyStarting = isLocallyRunning && bucket !== 'running';

              return (
                <Fragment key={step.id}>
                <TableRow className={cn('lot-row border-b border-gray-100 dark:border-slate-700')}>
                  {/* Lot */}
                  <TableCell className="py-0">
                    <div className="flex items-stretch">
                      {/* Filet vertical continu : marque le rattachement au groupe */}
                      <div className="ml-5 mr-4 w-px shrink-0 bg-gray-200 dark:bg-slate-700" />
                      <div className="min-w-0 flex items-center gap-1.5 py-1">
                        <span className="font-semibold text-sm leading-none text-gray-900 dark:text-gray-100 truncate">
                          {step.stepDefinition.name}
                        </span>
                        {step.stepDefinition.description && (
                          <InfoTooltip
                            content={step.stepDefinition.description}
                            contentClassName="max-w-sm"
                          >
                            <span className="inline-flex shrink-0 cursor-help text-gray-300 hover:text-gray-500 dark:text-slate-600 dark:hover:text-slate-400 transition-colors">
                              <Info className="h-3.5 w-3.5" />
                            </span>
                          </InfoTooltip>
                        )}
                        {LOTS_WITH_MAPPING_RULES.has(step.stepDefinition.code) && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setRulesStep(step);
                            }}
                            className="inline-flex shrink-0 items-center gap-1 rounded border border-gray-200 px-1.5 py-0.5 text-[10px] font-medium normal-case tracking-normal text-gray-500 transition-colors hover:border-violet-300 hover:text-violet-700 dark:border-slate-700 dark:text-slate-400 dark:hover:border-violet-700 dark:hover:text-violet-300"
                          >
                            <BookOpen className="h-3 w-3" />
                            Règles de mapping
                          </button>
                        )}
                        {additions > 0 && (
                          <AdditionsChip count={additions} countedAt={step.sourceCountedAt} />
                        )}
                        {LOT_WARNINGS[step.stepDefinition.code] && (
                          <InfoTooltip
                            contentClassName="max-w-sm"
                            content={
                              <>
                                <p className="mb-1 font-medium uppercase tracking-wide text-[10px] text-amber-600 dark:text-amber-400">
                                  Champs non migrés
                                </p>
                                <ul className="list-disc space-y-0.5 pl-4">
                                  {LOT_WARNINGS[step.stepDefinition.code].map((w, i) => (
                                    <li key={i}>{w}</li>
                                  ))}
                                </ul>
                              </>
                            }
                          >
                            <span className="inline-flex shrink-0 cursor-help items-center gap-1 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium normal-case tracking-normal text-amber-700 dark:border-amber-700/50 dark:bg-amber-900/20 dark:text-amber-400">
                              <AlertTriangle className="h-3 w-3" />
                              Champs non migrés
                            </span>
                          </InfoTooltip>
                        )}
                      </div>
                    </div>
                  </TableCell>

                  {/* Mapping — chip d'état cliquable ouvrant le drawer de mapping */}
                  <TableCell className="py-0 text-center">
                    {LOTS_WITH_MAPPING.has(step.stepDefinition.code) && onConfigureMapping ? (
                      <InfoTooltip content="Configurer le mapping V1 → V2 normalisé" wrap>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onConfigureMapping(step);
                          }}
                          className={cn(
                            'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium normal-case tracking-normal transition-colors',
                            mappingChipClass(mappingState)
                          )}
                        >
                          {mappingState === 'complete' ? (
                            <CheckCircle className="h-3 w-3" />
                          ) : (
                            <Settings2 className="h-3 w-3" />
                          )}
                          {mappingState === 'na' ? 'Mapping' : `Mapping ${mappingMapped}/${mappingTotal}`}
                        </button>
                      </InfoTooltip>
                    ) : (
                      <span className="text-xs text-gray-300">—</span>
                    )}
                  </TableCell>

                  {/* Progression — barre segmentée + détail */}
                  <TableCell className="py-0">
                    {(() => {
                      const total = step.totalRecords;
                      // Lancement demandé mais pas encore acté côté serveur : on
                      // remet la barre à 0 (pulsante) pour signaler qu'un nouveau
                      // run démarre. La progression réelle reprend dès que le
                      // premier chunk revient (statut → running).
                      if (isLocallyStarting) {
                        return (
                          <InfoTooltip content="Lancement en cours…">
                            <div className="flex items-center gap-2.5 w-full text-xs normal-case tracking-normal cursor-help">
                              <span className="w-14 shrink-0 text-right font-semibold tabular-nums text-slate-900 dark:text-slate-100">
                                0
                                {total > 0 && (
                                  <span className="text-slate-500 dark:text-slate-400 font-normal">/{total}</span>
                                )}
                              </span>
                              <div className="flex h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700 animate-pulse" />
                              <span className="w-14 shrink-0 text-right font-semibold tabular-nums text-slate-700 dark:text-slate-200">
                                0%
                              </span>
                            </div>
                          </InfoTooltip>
                        );
                      }
                      const isEstimating = estimatingStepId === step.id;
                      const canEstimate =
                        !!onEstimateLot &&
                        (step.status === 'pending' || step.status === 'skipped' || step.status === 'bypassed');
                      if (total === 0) {
                        // Rien d'extrait, mais la V1 s'est remplie depuis
                        // (école qui saisit ses inscriptions après coup) : on
                        // affiche le volume à reprendre, pas « aucune donnée ».
                        if (additions > 0) {
                          // Volumétrie = comptage V1 live : le lot n'a rien
                          // extrait, tout est à reprendre.
                          const liveTotal = step.sourceRecordCount ?? additions;
                          return (
                            <div className="flex items-center gap-2.5 w-full text-xs normal-case tracking-normal">
                              <span className="w-14 shrink-0 text-right font-semibold tabular-nums text-slate-900 dark:text-slate-100">
                                0
                                <span className="text-slate-500 dark:text-slate-400 font-normal">/{liveTotal}</span>
                              </span>
                              <div className="flex h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700" />
                              <span className="w-14 shrink-0 text-right font-semibold tabular-nums text-slate-700 dark:text-slate-200">
                                0%
                              </span>
                            </div>
                          );
                        }
                        // Estimé à 0 ligne → « non applicable » (rien à migrer).
                        // Jamais estimé → « non estimé » + bouton d'estimation.
                        if (step.countEstimatedAt) {
                          return (
                            <div className="flex items-center justify-center w-full">
                              <span className="text-xs text-slate-400 dark:text-slate-500 italic normal-case tracking-normal font-normal">
                                aucune donnée
                              </span>
                            </div>
                          );
                        }
                        return (
                          <div className="flex items-center justify-center gap-1.5 w-full">
                            <span className="text-xs text-slate-500 dark:text-slate-400 italic normal-case tracking-normal font-normal">
                              non estimé
                            </span>
                            {canEstimate && (
                              <InfoTooltip content="Estimer le volume de ce lot" wrap>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-auto py-0 px-1 text-gray-400 hover:text-violet-700"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (!isEstimating) onEstimateLot!(step);
                                  }}
                                  disabled={isEstimating}
                                >
                                  {isEstimating ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <RefreshCw className="h-3.5 w-3.5" />
                                  )}
                                </Button>
                              </InfoTooltip>
                            )}
                          </div>
                        );
                      }
                      const migrated = step.successRecords;
                      const failed = step.failedRecords;
                      const review = step.pendingReviewRecords;
                      // Exclus = volontairement écartés (raison tracée) : comptent
                      // comme traités, la barre peut donc atteindre 100 %.
                      const excluded = step.excludedRecords ?? 0;
                      const toMigrate = Math.max(0, total - migrated - failed - review - excluded);
                      const pctMigrated = (migrated / total) * 100;
                      const pctReview = (review / total) * 100;
                      const pctFailed = (failed / total) * 100;
                      const pctExcluded = (excluded / total) * 100;
                      // % affiché = taux de réussite (succès / total hors exclus),
                      // pas l'avancement du traitement : un lot terminé avec des
                      // erreurs n'affiche plus 100 %. Les exclus (décisions
                      // assumées) sont neutralisés, comme dans le rapport.
                      const effectiveTotal = total - excluded;
                      const pctSuccess = effectiveTotal > 0 ? (migrated / effectiveTotal) * 100 : 100;
                      const isRunning = bucket === 'running';
                      const breakdownItems = [
                        migrated > 0 && { label: 'Migrés', value: migrated, dot: 'bg-emerald-500' },
                        review > 0 && { label: 'À vérifier', value: review, dot: 'bg-amber-500' },
                        failed > 0 && { label: 'En erreur', value: failed, dot: 'bg-red-500' },
                        excluded > 0 && { label: 'Exclus', value: excluded, dot: 'bg-slate-400' },
                        toMigrate > 0 && { label: 'À migrer', value: toMigrate, dot: 'bg-slate-300' },
                      ].filter(Boolean) as { label: string; value: number; dot: string }[];
                      const breakdownContent = breakdownItems.length > 0 ? (
                        <ul className="space-y-1">
                          {breakdownItems.map((it) => (
                            <li key={it.label} className="flex items-center gap-1.5">
                              <span className={cn('h-1.5 w-1.5 rounded-full', it.dot)} />
                              <span>
                                {it.label} : <span className="font-medium tabular-nums">{it.value}</span>
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : null;
                      return (
                        <InfoTooltip content={breakdownContent}>
                        <div className="flex items-center gap-2.5 w-full text-xs normal-case tracking-normal cursor-help">
                          <span className="w-14 shrink-0 text-right font-semibold tabular-nums text-slate-900 dark:text-slate-100">
                            {migrated + review + failed + excluded}
                            <span className="text-slate-500 dark:text-slate-400 font-normal">/{total}</span>
                          </span>

                          {/* Barre segmentée */}
                          <div
                            className={cn(
                              'flex h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700',
                              isRunning && 'animate-pulse'
                            )}
                          >
                            {migrated > 0 && (
                              <div
                                className={migrated + excluded === total ? 'bg-emerald-500' : 'bg-orange-400'}
                                style={{ width: `${pctMigrated}%` }}
                              />
                            )}
                            {review > 0 && (
                              <div className="bg-amber-500" style={{ width: `${pctReview}%` }} />
                            )}
                            {failed > 0 && (
                              <div className="bg-red-500" style={{ width: `${pctFailed}%` }} />
                            )}
                            {excluded > 0 && (
                              <div className="bg-slate-400" style={{ width: `${pctExcluded}%` }} />
                            )}
                          </div>

                          <span
                            className={cn(
                              'w-14 shrink-0 text-right font-semibold tabular-nums',
                              failed > 0
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-slate-700 dark:text-slate-200'
                            )}
                          >
                            {formatPercent(pctSuccess)}%
                          </span>
                        </div>
                        </InfoTooltip>
                      );
                    })()}
                  </TableCell>

                  {/* Statut */}
                  <TableCell className="py-0 text-center">
                    {isEmpty ? null : isSkipped ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
                        <SkipForward className="h-3 w-3" />
                        Ignoré
                      </span>
                    ) : isBypassed ? (
                      <InfoTooltip
                        contentClassName="max-w-xs"
                        content="Lot contourné volontairement : les lots dépendants sont débloqués. Il reste relançable à tout moment (reprise idempotente)."
                        wrap
                      >
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700 cursor-help">
                          <FastForward className="h-3 w-3" />
                          Bypassé
                        </span>
                      </InfoTooltip>
                    ) : isBlocked && bucket === 'pending' ? (
                      <DepsTooltip deps={deps} blockReason={blockReason}>
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700 cursor-help">
                          <Lock className="h-3 w-3" />
                          Bloqué
                        </span>
                      </DepsTooltip>
                    ) : bucket === 'failed' && step.errorLog ? (
                      <InfoTooltip
                        contentClassName="max-w-md"
                        content={
                          <>
                            <p className="mb-1 font-medium uppercase tracking-wide text-[10px] text-red-500">
                              Erreur
                            </p>
                            <p className="whitespace-pre-wrap break-words">{step.errorLog}</p>
                          </>
                        }
                      >
                        <span
                          className={cn(
                            'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium cursor-help',
                            cfg.pill
                          )}
                        >
                          <span className={cn('h-1.5 w-1.5 rounded-full', cfg.dot)} />
                          {cfg.label}
                        </span>
                      </InfoTooltip>
                    ) : (
                      <DepsTooltip deps={deps}>
                        <span
                          className={cn(
                            'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium',
                            cfg.pill,
                            deps.length > 0 && 'cursor-help'
                          )}
                        >
                          <span className={cn('h-1.5 w-1.5 rounded-full', cfg.dot)} />
                          {cfg.label}
                        </span>
                      </DepsTooltip>
                    )}
                  </TableCell>

                  {/* Action */}
                  <TableCell className="py-0">
                    <div className="flex items-center justify-center gap-1 whitespace-nowrap">
                    {isEmpty ? null : (() => {
                      // Lancement demandé depuis cette table : on affiche le
                      // bouton animé et désactivé EN PREMIER, avant les branches
                      // completed/review. Sinon un lot relancé (bouton Relancer
                      // ↻ sur un lot migré) resterait cliquable sans feedback
                      // tant que le serveur n'a pas basculé son statut.
                      if (isLocallyRunning) {
                        return (
                          <Button variant="outline" size="sm" className="h-auto py-0 w-28" disabled>
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            En cours…
                          </Button>
                        );
                      }
                      // « Reprendre » dès qu'il y a déjà eu activité côté
                      // serveur : status `migrating` (run interrompu) ou des
                      // records déjà migrés/failed (success/failed/review > 0).
                      // L'idempotence garantit pas de doublon.
                      const hasStarted =
                        bucket === 'running' ||
                        step.successRecords > 0 ||
                        step.failedRecords > 0 ||
                        step.pendingReviewRecords > 0 ||
                        (step.excludedRecords ?? 0) > 0;

                      if (bucket === 'completed') {
                        const isReconcilableLot = step.stepDefinition.reconcilable === true;
                        return (
                          <>
                            <Button variant="outline" size="sm" className="h-auto py-0 w-28" onClick={() => onView(step)}>
                              <Eye className="h-3.5 w-3.5" />
                              Vérifier
                            </Button>
                            {isReconcilableLot && (
                              <InfoTooltip
                                wrap
                                contentClassName="max-w-xs"
                                content="Relancer : rejoue le lot sur les enregistrements déjà migrés pour propager les évolutions de la V1 (fiches mises à jour) et rattraper les liens manqués. Additif et idempotent — sans doublon ni reset."
                              >
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-auto py-0 px-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onRun(step, true);
                                  }}
                                  disabled={!canReconcile || runningGroup != null}
                                >
                                  <RefreshCw className="h-3.5 w-3.5" />
                                </Button>
                              </InfoTooltip>
                            )}
                          </>
                        );
                      }
                      if (bucket === 'review') {
                        return (
                          <>
                            <Button variant="outline" size="sm" className="h-auto py-0 w-28" onClick={() => onView(step)}>
                              <CheckCircle className="h-3.5 w-3.5" />
                              Examiner
                            </Button>
                            <InfoTooltip
                              wrap
                              contentClassName="max-w-xs"
                              content="Relancer le lot : ré-extrait les données V1 et retente les lignes en échec. Idempotent — les lignes déjà migrées sont conservées, sans doublon. Écrase les corrections manuelles non migrées."
                            >
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-auto py-0 px-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  onRun(step);
                                }}
                                disabled={!canRun || runningGroup != null}
                              >
                                <RefreshCw className="h-3.5 w-3.5" />
                              </Button>
                            </InfoTooltip>
                          </>
                        );
                      }
                      if (isBlocked) {
                        return (
                          <>
                            <DepsTooltip deps={deps} blockReason={blockReason}>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-auto py-0 w-28"
                                disabled
                              >
                                <Lock className="h-3.5 w-3.5" />
                                {hasStarted ? 'Reprendre' : 'Migrer'}
                              </Button>
                            </DepsTooltip>
                            {hasStarted && (
                              <InfoTooltip
                                wrap
                                contentClassName="max-w-xs"
                                content="Examiner les enregistrements du lot (déjà migrés, en échec, à vérifier)."
                              >
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-auto py-0 px-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onView(step);
                                  }}
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                </Button>
                              </InfoTooltip>
                            )}
                          </>
                        );
                      }
                      if (hasStarted) {
                        return (
                          <>
                            <InfoTooltip
                              wrap
                              contentClassName="max-w-xs"
                              content="Une migration précédente a été interrompue ou partiellement échouée — reprendre là où on s'était arrêté (pas de doublon)"
                            >
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-auto py-0 w-28"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  onRun(step);
                                }}
                                disabled={!canRun || runningGroup != null}
                              >
                                <Play className="h-3.5 w-3.5" />
                                Reprendre
                              </Button>
                            </InfoTooltip>
                            <InfoTooltip
                              wrap
                              contentClassName="max-w-xs"
                              content="Examiner les enregistrements du lot (déjà migrés, en échec, à vérifier)."
                            >
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-auto py-0 px-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  onView(step);
                                }}
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </Button>
                            </InfoTooltip>
                          </>
                        );
                      }
                      return (
                        <DepsTooltip deps={deps}>
                          <Button
                            type="button"
                            size="sm"
                            className="h-auto py-0 w-28"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onRun(step);
                            }}
                            disabled={!canRun || runningGroup != null}
                          >
                            <Play className="h-3.5 w-3.5" />
                            Migrer
                          </Button>
                        </DepsTooltip>
                      );
                    })()}
                    {/* Bypasser : contourne un lot incomplet pour débloquer les
                        lots dépendants. Masqué si le lot est migré, déjà
                        bypassé, vide ou en cours. La confirmation est portée
                        par le handler (dialog côté page).
                        TODO: masqué temporairement (bouton désactivé) — remettre
                        `false &&` à retirer pour réactiver. */}
                    {false &&
                      !isEmpty &&
                      !!onBypass &&
                      !isBypassed &&
                      bucket !== 'completed' &&
                      bucket !== 'running' &&
                      runningStepId !== step.id && (
                        <InfoTooltip
                          wrap
                          contentClassName="max-w-xs"
                          content="Bypasser ce lot : les lots dépendants seront débloqués même s'il est incomplet. Relançable plus tard."
                        >
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-auto py-0 px-1.5 text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onBypass?.(step);
                            }}
                            disabled={runningGroup != null}
                          >
                            <FastForward className="h-3.5 w-3.5" />
                          </Button>
                        </InfoTooltip>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                {/* Sous-ligne : avancement de l'import asynchrone V2 des
                    valeurs de champs (rendue seulement si des jobs existent). */}
                {isUserLotCode(step.stepDefinition.code) && !isEmpty && (
                  <FieldValueJobsRow
                    step={step}
                    stepIsRunning={bucket === 'running' || isLocallyRunning}
                    canRetry={canRun || canReconcile}
                  />
                )}
                </Fragment>
              );
            });
            return groupErrorRow
              ? [headerRow, groupErrorRow, ...stepRows]
              : [headerRow, ...stepRows];
            })
          )}
        </TableBody>
      </Table>

      <MappingRulesDialog
        open={rulesStep !== null}
        lotCode={rulesStep?.stepDefinition.code ?? null}
        lotName={rulesStep?.stepDefinition.name}
        onClose={() => setRulesStep(null)}
      />
    </TooltipProvider>
  );
}
